"use client";

import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const highlights = ["FDACS Licensed & Fully Insured", "Locally Owned in Punta Gorda", "Eco-Friendly Treatment Options", "100% Satisfaction Guarantee"];
export default function TheGoldStandardOfPaving() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="222734" id="the-peace-river-mission" className="bg-background py-24 md:py-32 px-6 md:px-16 overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
          {}
          <div className={`lg:col-span-4 flex justify-center lg:justify-start transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="flex flex-col items-center justify-center leading-[0.75] text-[10rem] md:text-[14rem] lg:text-[18rem] font-bold tracking-tighter text-transparent opacity-20 select-none [-webkit-text-stroke:2px_var(--color-primary)]">
              
              
              
            </div>
          </div>

          {}
          <div className="lg:col-span-8 flex flex-col justify-center">
            <div className={`transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary font-default mb-8 tracking-tight">
                The Peace River Mission
              </h2>
              <p className="text-lg md:text-xl text-primary/90 mb-12 max-w-2xl leading-relaxed font-serif">
                Since our founding in Punta Gorda, Peace River Pest Control has been dedicated to protecting Florida homes with uncompromising safety and local expertise. We combine advanced entomology with a deep commitment to our community, ensuring that every family from Port Charlotte to Cape Coral can enjoy a pest-free environment built on trust and reliability.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {highlights.map((highlight, i) => <div key={i} data-index={i} className={`flex items-start gap-4 transition-all duration-700 ease-out`} style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transitionDelay: `${500 + i * 150}ms`
            }}>
                  <div className="flex-shrink-0 w-8 h-8 rounded-full border border-primary flex items-center justify-center mt-0.5 bg-primary/10">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-primary font-semibold text-lg font-default">
                    {highlight}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}